import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import Alert from 'shared/ui/Alert/Alert';
import ModalBodyWrapper from 'shared/ui/Modal/ModalBodyWrapper';
import ModalBlockButton from 'shared/ui/Button/ModalBlockButton/ModalBlockButton';
import {CollapsiblePanel} from 'entities/tariff';
import FormGroupWithError from 'shared/ui/FormGroup/FormGroupWithError';
import HintQuestion from 'shared/ui/Hint/HintQuestion';
import InputText from 'shared/ui/Input/InputText';
import RadioGroup from 'shared/ui/RadioGroup/RadioGroup';
import {TagsChipField, CheckInterval, GeoCheck} from 'entities/monitor';
import {NotifyChannels} from 'entities/notification';
import TimeoutSlider from 'shared/ui/Slider/TimeoutSlider';
import IPortMonitor from 'features/port/model/PortMonitor/IPortMonitor';
import {usePortMonitorForm} from 'features/port/model/usePortMonitorForm';
import {PORT_DEFAULT_TIMEOUT, PORT_NAME_MAX_LENGTH} from 'features/port/lib/portMonitorFormHelper';
import {isValidPort, isValidPortMonitorIp} from 'features/port/lib/portMonitorHelper';

import styles from './PortMonitorForm.module.scss';

interface IPortMonitorFormProps {
    item?: IPortMonitor,
    onSuccess: () => void,
    onLimitExceeded?: () => void,
    onClose?: () => void,
}

const PortMonitorForm: React.FC<IPortMonitorFormProps> = ({
    item,
    onSuccess,
    onLimitExceeded,
    onClose,
}) => {
    const {form, isNew, isSubmitting, fieldErrors, commonError, namePlaceholder, submit} = usePortMonitorForm({
        item,
        onSuccess,
        onLimitExceeded,
    });
    const {register, formState, control, setValue, watch} = form;
    const {errors, isValid} = formState;

    return (
        <form onSubmit={submit} className={styles.form}>
            <ModalBodyWrapper>
                {commonError && <Alert>{commonError}</Alert>}

                <FormGroupWithError
                    error={errors?.ip?.message ?? fieldErrors.ip}
                    className={cn(styles.formGroup, isNew && styles.firstFormGroup)}
                >
                    <InputText
                        name="ip"
                        placeholder={t`IP-адрес`}
                        register={register}
                        control={control}
                        rules={{
                            required: t`Укажите IP-адрес`,
                            validate: (value: string) =>
                                isValidPortMonitorIp(value) || t`Укажите корректный IPv4- или IPv6-адрес`,
                        }}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    error={errors?.port?.message ?? fieldErrors.port}
                    className={styles.formGroup}
                >
                    <InputText
                        name="port"
                        inputMode="numeric"
                        placeholder={t`Порт`}
                        register={register}
                        control={control}
                        rules={{
                            required: t`Укажите порт`,
                            validate: (value: string) => isValidPort(value) || t`Порт должен быть от 1 до 65535`,
                        }}
                    />
                </FormGroupWithError>

                <FormGroupWithError
                    error={errors?.name?.message ?? fieldErrors.name}
                    className={styles.formGroup}
                >
                    <InputText
                        name="name"
                        placeholder={namePlaceholder}
                        register={register}
                        control={control}
                        validate={false}
                        rules={{
                            maxLength: {
                                value: PORT_NAME_MAX_LENGTH,
                                message: t`Название не длиннее 255 символов`,
                            },
                        }}
                    />
                </FormGroupWithError>

                <TagsChipField
                    control={control}
                    label={t`Теги`}
                    error={errors?.tags?.message ?? fieldErrors.tags}
                    className={styles.formGroup}
                />

                <div className={styles.block}>
                    <div className={styles.label}>
                        {t`Желаемое состояние`}
                        <HintQuestion placement="top-start" className={styles.icon}>
                            {t`Инцидент откроется, когда порт окажется не в этом состоянии.`}
                        </HintQuestion>
                    </div>
                    <RadioGroup
                        name="desired_state"
                        control={control}
                        options={[
                            {value: 'open', label: t`Открыт`},
                            {value: 'closed', label: t`Закрыт`},
                        ]}
                    />
                </div>

                <CheckInterval item={item} setValue={setValue} watch={watch} errors={errors}/>
                {fieldErrors.check_interval && <Alert>{fieldErrors.check_interval}</Alert>}

                <div className={styles.block}>
                    <div className={styles.label}>
                        {t`Таймаут проверки`}
                    </div>
                    <FormGroupWithError error={fieldErrors.timeout}>
                        <TimeoutSlider
                            name="timeout"
                            setValue={setValue}
                            defaultValue={item?.timeout ?? PORT_DEFAULT_TIMEOUT}
                        />
                    </FormGroupWithError>
                </div>

                <GeoCheck item={item} control={control} setValue={setValue}/>
                {fieldErrors.locations && <Alert>{fieldErrors.locations}</Alert>}
                {fieldErrors.threshold && <Alert>{fieldErrors.threshold}</Alert>}
            </ModalBodyWrapper>

            <ModalBodyWrapper>
                <CollapsiblePanel
                    title={t`Уведомления`}
                    defaultExpanded={false}
                    className={styles.panel}
                >
                    <NotifyChannels
                        control={control}
                        register={register}
                        emailFieldName="notify_by_email"
                        additionalEmailsFieldName="additional_emails"
                        telegramFieldName="telegram_chats"
                        maxFieldName="max_chats"
                    />
                </CollapsiblePanel>
            </ModalBodyWrapper>

            <ModalBlockButton
                primaryButtonText={isNew ? t`Добавить` : t`Сохранить`}
                primaryButtonType="submit"
                primaryButtonLoading={isSubmitting}
                primaryButtonDisabled={!isValid}
                secondaryButtonOnClick={onClose}
            />
        </form>
    );
};

export default PortMonitorForm;
